import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { collection, query, where, getDocs, doc, updateDoc, documentId } from 'firebase/firestore';
import { db } from '../firebase';
import { LINE_STATUSES } from '../firebase/schema';
import { useFacility } from '../context/FacilityContext';
import { FACILITY_IDS, filterByFacility, resolveFacilityId } from '../utils/facilities';
import { 
  Box, 
  Typography, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  CircularProgress,
  TextField,
  Alert,
  IconButton
} from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import JobSticker from '../components/JobSticker';
import ArrivalConfirmationModal from '../components/ArrivalConfirmationModal';
import { syncOrderStatusFromLines, maybeFinalizeSampleOrderAfterReceive } from '../utils/orderStatusSync';
import { isSampleOrder, sampleLineReceiveFields } from '../utils/sampleOrder';
import { spPrintBlockReasonForCustomerLines } from '../utils/spLicenseStore';
import { lineIsFinalized } from '../utils/lineCutStatus';
import { fetchStickerPrintPayload } from '../utils/fetchStickerPrintPayload';
import { activityLogAppend } from '../utils/lineActivityLog';
import { printLabelBase64 } from '../utils/printServer';

const theme = createTheme({
  palette: {
    mode: 'dark',
    background: {
      default: '#1C1C1C',
      paper: '#2C2C2C'
    },
    primary: {
      main: '#98FB98'
    },
    text: {
      primary: '#FFFFFF',
      secondary: 'rgba(255, 255, 255, 0.7)'
    }
  },
  typography: {
    fontFamily: "'Montserrat', sans-serif",
    button: {
      textTransform: 'uppercase'
    }
  },
  components: {
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderColor: 'rgba(255, 255, 255, 0.1)',
          padding: '8px 16px',
          fontSize: '0.85rem',
          textTransform: 'uppercase'
        },
        head: {
          fontWeight: 600,
          backgroundColor: '#1C1C1C'
        }
      }
    }
  }
});

const StyledTableCell = styled(TableCell)`
  padding: 12px 16px;
  &.size-cell {
    padding: 12px 4px;
    width: 48px;
    min-width: 48px;
    text-align: center;
  }
  &.checkbox-cell {
    width: 48px;
    min-width: 48px;
    padding: 8px;
    text-align: center;
  }
  &.design-cell {
    min-width: 240px;
    padding-left: 0;
  }
  &.style-cell {
    min-width: 180px;
  }
  &.color-cell {
    min-width: 120px;
  }
  &.total-cell {
    width: 80px;
    text-align: center;
  }
  &.order-info {
    color: rgba(255, 255, 255, 0.7);
    font-size: 0.8rem;
  }
`;

const SearchBox = styled(Box)`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
`;

const SuccessAlert = styled(Alert)`
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 1000;
`;

const ExpectedBanner = styled(Box)`
  background: linear-gradient(135deg, rgba(var(--al-primary-rgb), 0.15) 0%, rgba(var(--al-primary-rgb), 0.05) 100%);
  border: 2px solid rgba(var(--al-primary-rgb), 0.3);
  border-radius: 8px;
  padding: 16px 20px;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const SIZE_COLUMNS = ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL', '5XL'];

const RECEIVING_LINE_STATUSES = [LINE_STATUSES.ORDERED, LINE_STATUSES.CUT_NOT_IN];
// Firestore `in` filters accept at most 30 values.
const ORDER_ID_CHUNK = 30;

const mapDocs = (snap) => snap.docs.map((d) => ({ id: d.id, ...d.data() }));

const openReceivingLinesQuery = () =>
  query(collection(db, 'orderLines'), where('status', 'in', RECEIVING_LINE_STATUSES));

/**
 * Open receiving lines for one facility.
 * Strawberry Patch is a direct facilityId query.
 * Central also keeps older lines that were never given a facilityId — Firestore
 * cannot query a missing field, so that case reads the open queue and drops
 * the other shop. Missing facilityId is treated as Central.
 */
const fetchReceivingLines = async (activeFacilityId) => {
  const facility = resolveFacilityId(activeFacilityId);
  const linesRef = collection(db, 'orderLines');

  if (facility !== FACILITY_IDS.CENTRAL) {
    try {
      const snap = await getDocs(
        query(
          linesRef,
          where('facilityId', '==', facility),
          where('status', 'in', RECEIVING_LINE_STATUSES)
        )
      );
      return mapDocs(snap);
    } catch (error) {
      if (error?.code !== 'failed-precondition') throw error;
      console.warn(
        'Receiving facility index is not ready. Loading open receiving lines and filtering by facility in the browser.',
        error
      );
    }
  }

  const snap = await getDocs(openReceivingLinesQuery());
  return mapDocs(snap).filter(
    (line) => resolveFacilityId(line.facilityId) === facility
  );
};

const fetchOrdersByIds = async (orderIds) => {
  const ids = [...new Set(orderIds.filter(Boolean))];
  if (ids.length === 0) return {};

  const chunks = [];
  for (let i = 0; i < ids.length; i += ORDER_ID_CHUNK) {
    chunks.push(ids.slice(i, i + ORDER_ID_CHUNK));
  }

  const snaps = await Promise.all(
    chunks.map((chunk) =>
      getDocs(query(collection(db, 'orders'), where(documentId(), 'in', chunk)))
    )
  );

  const ordersData = {};
  snaps.forEach((snap) => {
    mapDocs(snap).forEach((order) => {
      ordersData[order.id] = order;
    });
  });
  return ordersData;
};

// Custom Checkbox component
const CustomCheckbox = styled.div`
  width: 18px;
  height: 18px;
  border: 2px solid rgba(var(--al-primary-rgb), 0.5);
  border-radius: 2px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  margin: 0 auto;

  ${props => props.checked && `
    background-color: var(--al-primary);
    border-color: var(--al-primary);
  `}

  ${props => props.disabled && `
    opacity: 0.5;
    cursor: not-allowed;
  `}

  &:hover {
    border-color: ${props => !props.disabled && 'var(--al-primary)'};
  }

  svg {
    width: 14px;
    height: 14px;
    fill: #222;
    opacity: ${props => props.checked ? 1 : 0};
  }
`;

const ReceivingDashboard = () => {
  const { facilityId } = useFacility();
  const [lines, setLines] = useState([]);
  const [orders, setOrders] = useState({});
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [checkedLines, setCheckedLines] = useState({});
  
  // Modal state
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [selectedLine, setSelectedLine] = useState(null);
  const [stickerToPrint, setStickerToPrint] = useState(null);
  const [isPrinting, setIsPrinting] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const fetchLinesAndOrders = async () => {
      setLoading(true);
      try {
        // Ordered, plus screens cut before the garments arrived.
        // Scoped to the active facility so the other shop's queue is not downloaded.
        const linesList = await fetchReceivingLines(facilityId);
        const ordersData = await fetchOrdersByIds(linesList.map((line) => line.orderId));
        if (cancelled) return;

        const facilityLines = filterByFacility(
          linesList.map((line) => ({
            ...line,
            facilityId: line.facilityId || ordersData[line.orderId]?.facilityId
          })),
          facilityId
        );

        // Smart sorting: prioritize by expected delivery date
        const sortedLines = facilityLines.sort((a, b) => {
          // Lines with expectedDeliveryDate come first, sorted by date (soonest first)
          const aHasDate = !!a.expectedDeliveryDate;
          const bHasDate = !!b.expectedDeliveryDate;
          
          if (aHasDate && bHasDate) {
            const aTime = a.expectedDeliveryDate.seconds * 1000;
            const bTime = b.expectedDeliveryDate.seconds * 1000;
            return aTime - bTime; // Soonest first
          }
          
          if (aHasDate && !bHasDate) return -1; // a comes first
          if (!aHasDate && bHasDate) return 1;  // b comes first
          
          // If neither has delivery date, sort by orderedAt (oldest first)
          const aTime = a.orderedAt?.seconds ? a.orderedAt.seconds * 1000 : a.orderedAt?.getTime() || 0;
          const bTime = b.orderedAt?.seconds ? b.orderedAt.seconds * 1000 : b.orderedAt?.getTime() || 0;
          return aTime - bTime;
        });

        const scopedOrderIds = new Set(sortedLines.map((l) => l.orderId));
        const scopedOrders = Object.fromEntries(
          Object.entries(ordersData).filter(([id]) => scopedOrderIds.has(id))
        );

        setLines(sortedLines);
        setOrders(scopedOrders);
        setCheckedLines({}); // Reset checked state when lines change
      } catch (error) {
        console.error('Error fetching data:', error);
      }
      if (!cancelled) setLoading(false);
    };
    fetchLinesAndOrders();
    return () => {
      cancelled = true;
    };
  }, [facilityId]);

  // Open confirmation modal
  const handleCheckClick = (line) => {
    setSelectedLine(line);
    setConfirmModalOpen(true);
  };

  // Close modal
  const handleModalClose = () => {
    setConfirmModalOpen(false);
    setSelectedLine(null);
  };

  // Mark as IN only (no sticker)
  const handleMarkAsIn = async () => {
    if (!selectedLine) return;
    
    const lineId = selectedLine.id;
    setUpdating(prev => ({ ...prev, [lineId]: true }));
    
    try {
      const lineRef = doc(db, 'orderLines', lineId);
      const order = orders[selectedLine.orderId];
      const sample = isSampleOrder(order);
      if (sample) {
        const blockReason = await spPrintBlockReasonForCustomerLines(db, order?.customerId, [selectedLine]);
        if (blockReason) {
          alert(blockReason);
          setUpdating((prev) => ({ ...prev, [lineId]: false }));
          return;
        }
        await updateDoc(lineRef, {
          ...sampleLineReceiveFields(),
          countedInAt: new Date()
        });
      } else {
        const newStatus = lineIsFinalized(selectedLine)
          ? selectedLine.status
          : (selectedLine.isCut ? LINE_STATUSES.READY_TO_PRINT : LINE_STATUSES.IN_NOT_CUT);
        await updateDoc(lineRef, {
          status: newStatus,
          isIn: true,
          countedInAt: new Date(),
          ...activityLogAppend('Marked as IN', { from: selectedLine.status, to: newStatus })
        });
      }
      await syncOrderStatusFromLines(db, selectedLine.orderId);
      await maybeFinalizeSampleOrderAfterReceive(db, selectedLine.orderId);
      
      // Update local state
      setLines(prev => prev.filter(line => line.id !== lineId));
      setSuccessMessage(sample ? 'Sample line received — customer will be notified when all lines are in' : 'Line marked as received');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error updating line:', error);
      alert('Failed to update line');
    }
    
    setUpdating(prev => ({ ...prev, [lineId]: false }));
    handleModalClose();
  };

  // Mark as IN and print sticker
  const handleMarkAsInAndPrint = async () => {
    if (!selectedLine) return;
    
    const lineId = selectedLine.id;
    
    setUpdating(prev => ({ ...prev, [lineId]: true }));
    setIsPrinting(true);
    
    try {
      // Re-read line + order so the sticker reflects edits made after this page loaded
      const { line: freshLine, order: freshOrder } = await fetchStickerPrintPayload(
        db,
        lineId,
        selectedLine.orderId
      );
      const order = freshOrder || orders[selectedLine.orderId];
      const lineForStatus = freshLine || selectedLine;

      // First update the database
      const lineRef = doc(db, 'orderLines', lineId);
      const sample = isSampleOrder(order);
      if (sample) {
        const blockReason = await spPrintBlockReasonForCustomerLines(db, order?.customerId, [lineForStatus]);
        if (blockReason) {
          alert(blockReason);
          setUpdating((prev) => ({ ...prev, [lineId]: false }));
          setIsPrinting(false);
          return;
        }
        await updateDoc(lineRef, {
          ...sampleLineReceiveFields(),
          countedInAt: new Date()
        });
      } else {
        const newStatus = lineIsFinalized(lineForStatus)
          ? lineForStatus.status
          : (lineForStatus.isCut ? LINE_STATUSES.READY_TO_PRINT : LINE_STATUSES.IN_NOT_CUT);
        await updateDoc(lineRef, {
          status: newStatus,
          isIn: true,
          countedInAt: new Date(),
          ...activityLogAppend('Marked as IN', { from: lineForStatus.status, to: newStatus })
        });
      }
      await syncOrderStatusFromLines(db, lineForStatus.orderId || selectedLine.orderId);
      await maybeFinalizeSampleOrderAfterReceive(db, lineForStatus.orderId || selectedLine.orderId);
      
      // Print from fresh Firestore data (not the list cache)
      setStickerToPrint({
        line: freshLine,
        order
      });
      
      // Update local state
      setLines(prev => prev.filter(line => line.id !== lineId));
      setSuccessMessage('Line marked as received - printing sticker...');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error updating line:', error);
      alert('Failed to update line');
      setIsPrinting(false);
    }
    
    setUpdating(prev => ({ ...prev, [lineId]: false }));
    handleModalClose();
  };

  // Calculate expected today count
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  
  const expectedToday = lines.filter(line => {
    if (!line.expectedDeliveryDate) return false;
    const deliveryDate = new Date(line.expectedDeliveryDate.seconds * 1000);
    return deliveryDate >= today && deliveryDate < tomorrow;
  });
  
  // Filter lines based on search term
  const filteredLines = lines.filter(line => {
    const searchLower = searchTerm.toLowerCase();
    const order = orders[line.orderId];
    return (
      line.designName?.toLowerCase().includes(searchLower) ||
      line.garmentId?.toLowerCase().includes(searchLower) ||
      line.garmentColor?.toLowerCase().includes(searchLower) ||
      order?.orderNumber?.toLowerCase().includes(searchLower) ||
      order?.customerName?.toLowerCase().includes(searchLower)
    );
  });

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ width: '100%', padding: '80px 20px 20px 20px', backgroundColor: 'var(--al-bg)', minHeight: '100vh' }}>
        <Typography variant="h4" sx={{ color: 'var(--al-primary)', fontWeight: 600, mb: 3 }}>Receiving Dashboard</Typography>
        
        {expectedToday.length > 0 && (
          <ExpectedBanner>
            <Typography sx={{ fontSize: '2rem' }}>📦</Typography>
            <Box>
              <Typography sx={{ fontWeight: 600, color: 'var(--al-primary)', fontSize: '1.1rem' }}>
                {expectedToday.length} Line{expectedToday.length !== 1 ? 's' : ''} Expected Today
              </Typography>
              <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.9rem' }}>
                {expectedToday.reduce((sum, line) => sum + (line.totalQuantity || 0), 0)} total pieces arriving
              </Typography>
            </Box>
          </ExpectedBanner>
        )}
        
        <SearchBox>
          <TextField
            placeholder="Search by invoice #, customer, design, style, color..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            fullWidth
            variant="outlined"
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'rgba(255, 255, 255, 0.7)' }} />,
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                color: 'white',
                '& fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.23)',
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.4)',
                },
              },
            }}
          />
        </SearchBox>

        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress sx={{ color: 'var(--al-primary)' }} />
            <Typography ml={2}>Loading lines...</Typography>
          </Box>
        ) : filteredLines.length === 0 ? (
          <Typography>No lines are currently waiting to be received.</Typography>
        ) : (
          <TableContainer component={Paper} sx={{ backgroundColor: 'var(--al-surface)', boxShadow: 'none', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell 
                    sx={{ 
                      width: '48px',
                      padding: '8px',
                      textAlign: 'center'
                    }}
                  ></TableCell>
                  <StyledTableCell className="design-cell">DESIGN NAME</StyledTableCell>
                  <StyledTableCell className="style-cell">STYLE</StyledTableCell>
                  <StyledTableCell className="color-cell">COLOR</StyledTableCell>
                  <StyledTableCell sx={{ minWidth: '120px' }}>ORDERED ON</StyledTableCell>
                  <StyledTableCell sx={{ minWidth: '180px' }}>TRACKING</StyledTableCell>
                  {SIZE_COLUMNS.map(size => (
                    <StyledTableCell key={size} className="size-cell">{size}</StyledTableCell>
                  ))}
                  <StyledTableCell className="total-cell">TOTAL</StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredLines.map(line => {
                  const order = orders[line.orderId];
                  return (
                    <TableRow 
                      key={line.id} 
                      hover 
                      sx={{ 
                        '&:hover': { 
                          backgroundColor: 'rgba(255, 255, 255, 0.05)' 
                        }
                      }}
                    >
                      <TableCell 
                        sx={{ 
                          width: '48px',
                          padding: '8px',
                          textAlign: 'center'
                        }}
                      >
                        <CustomCheckbox
                          checked={!!checkedLines[line.id]}
                          disabled={!!updating[line.id]}
                          onClick={() => !updating[line.id] && handleCheckClick(line)}
                        >
                          <svg viewBox="0 0 24 24">
                            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                          </svg>
                        </CustomCheckbox>
                      </TableCell>
                      <StyledTableCell className="design-cell">
                        {line.designName}
                        <Typography className="order-info" component="div">
                          {order?.customerName}
                          <br />
                        </Typography>
                      </StyledTableCell>
                      <StyledTableCell className="style-cell">{line.garmentId}</StyledTableCell>
                      <StyledTableCell className="color-cell">{line.garmentColor}</StyledTableCell>
                      <StyledTableCell>
                        {line.orderedAt ? (
                          new Date(line.orderedAt?.seconds ? line.orderedAt.seconds * 1000 : line.orderedAt).toLocaleDateString()
                        ) : (
                          <Typography sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>—</Typography>
                        )}
                      </StyledTableCell>
                      <StyledTableCell>
                        {line.trackingNumbers && line.trackingNumbers.length > 0 ? (
                          line.trackingNumbers.map((tracking, idx) => (
                            <Typography key={idx} sx={{ fontSize: '0.75rem', color: 'var(--al-primary)', cursor: 'pointer' }}>
                              📦 {tracking}
                            </Typography>
                          ))
                        ) : line.supplierOrderNumber ? (
                          <Typography className="order-info" component="div">
                            S&S #{line.supplierOrderNumber}
                          </Typography>
                        ) : (
                          <Typography sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>—</Typography>
                        )}
                      </StyledTableCell>
                      {SIZE_COLUMNS.map(size => (
                        <StyledTableCell key={size} className="size-cell">
                          {line.sizes?.[size] || '-'}
                        </StyledTableCell>
                      ))}
                      <StyledTableCell className="total-cell">
                        {line.totalQuantity}
                      </StyledTableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {successMessage && (
          <SuccessAlert
            severity="success"
            action={
              <IconButton
                aria-label="close"
                color="inherit"
                size="small"
                onClick={() => setSuccessMessage('')}
              >
                <CloseIcon fontSize="inherit" />
              </IconButton>
            }
          >
            {successMessage}
          </SuccessAlert>
        )}

        {/* Confirmation Modal */}
        <ArrivalConfirmationModal
          open={confirmModalOpen}
          onClose={handleModalClose}
          line={selectedLine}
          order={selectedLine ? orders[selectedLine.orderId] : null}
          onMarkAsIn={handleMarkAsIn}
          onMarkAsInAndPrint={handleMarkAsInAndPrint}
          isUpdating={updating[selectedLine?.id]}
          isPrinting={isPrinting}
        />

        {/* Hidden JobSticker for printing */}
        {stickerToPrint && (
          <div style={{ display: 'none' }}>
            <JobSticker
              line={stickerToPrint.line}
              order={stickerToPrint.order}
              onPdfGenerated={async (pdfData) => {
                if (!pdfData) {
                  setIsPrinting(false);
                  setStickerToPrint(null);
                  return;
                }
                try {
                  const result = await printLabelBase64(pdfData, { type: 'line', tray: 1 });
                  console.log('Print job submitted successfully:', result.jobId);
                  setSuccessMessage('Sticker printed successfully!');
                  setTimeout(() => setSuccessMessage(''), 3000);
                } catch (error) {
                  console.error('Print error details:', error);
                  alert(`Failed to print: ${error.message}`);
                } finally {
                  setIsPrinting(false);
                  setStickerToPrint(null);
                }
              }}
            />
          </div>
        )}
      </Box>
    </ThemeProvider>
  );
};

export default ReceivingDashboard; 